import { useEffect, useRef, useState } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { AutoComplete } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useNavigate } from 'react-router-dom';
import StockService from '../../services/StockService';
import ProductService from '../../services/ProductService';
import WarehouseService from '../../services/WarehouseService';

const movementTypeLabels = {
    1: { label: 'AÇILIŞ STOKU', severity: 'info', icon: 'pi pi-plus-circle' },
    2: { label: 'ALIŞ', severity: 'success', icon: 'pi pi-shopping-cart' },
    3: { label: 'SATIŞ', severity: 'danger', icon: 'pi pi-shopping-bag' },
    4: { label: 'MÜŞTERİ İADESİ', severity: 'success', icon: 'pi pi-refresh' },
    5: { label: 'TEDARİKÇİ İADESİ', severity: 'danger', icon: 'pi pi-refresh' },
    6: { label: 'MANUEL GİRİŞ', severity: 'info', icon: 'pi pi-arrow-down-left' },
    7: { label: 'MANUEL ÇIKIŞ', severity: 'warning', icon: 'pi pi-arrow-up-right' },
    8: { label: 'SAYIM DÜZELTME', severity: 'warning', icon: 'pi pi-pencil' },
    9: { label: 'FİRE / ZAYİ', severity: 'danger', icon: 'pi pi-trash' },
    10: { label: 'TRANSFER (ÇIKIŞ)', severity: 'warning', icon: 'pi pi-send' },
    11: { label: 'TRANSFER (GİRİŞ)', severity: 'info', icon: 'pi pi-download' }
};

export default function StockMovementListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        warehouseId: null
    });
    const toast = useRef(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {
                startDate: filters.startDate?.toISOString(),
                endDate: filters.endDate?.toISOString(),
                productId: selectedProduct?.id,
                warehouseId: filters.warehouseId
            };
            const res = await StockService.getStockMovements(params);
            if (res.data.success) {
                setData(res.data.data);
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'Veriler yüklenemedi' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        loadLookups();
    }, []);

    const loadLookups = async () => {
        try {
            const warRes = await WarehouseService.getAll();
            const warData = warRes.data?.data || warRes.data;
            if (warData) {
                setWarehouses(warData.map(w => ({ label: w.name, value: w.id })));
            }
        } catch (err) {
            console.error('Lookup load error', err);
        }
    };

    const searchProducts = async (event) => {
        if (event.query.trim().length < 3) {
            setFilteredProducts([]);
            return;
        }

        try {
            const res = await ProductService.getAutoComplete(event.query);
            const productData = res.data?.data || res.data;
            if (productData && Array.isArray(productData)) {
                setFilteredProducts(productData.map(p => ({
                    id: p.id,
                    name: p.name,
                    barcode: p.barcode,
                    label: `${p.name} (${p.barcode || 'Barkodsuz'})`
                })));
            }
        } catch (error) {
            console.error('Ürün arama hatası:', error);
        }
    };

    const directionTemplate = (rowData) => {
        const isIn = rowData.direction === 1;
        return (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl w-fit ${isIn ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                <i className={`pi ${isIn ? 'pi-arrow-down-left' : 'pi-arrow-up-right'} text-xs font-bold`}></i>
                <span className="text-[10px] font-black uppercase tracking-widest">{isIn ? 'GİRİŞ' : 'ÇIKIŞ'}</span>
            </div>
        );
    };

    const typeTemplate = (rowData) => {
        const typeInfo = movementTypeLabels[rowData.movementType] || { label: 'BİLİNMEYEN', severity: 'info', icon: 'pi pi-info-circle' };
        return (
            <div className="flex items-center gap-2">
                <i className={`${typeInfo.icon} text-slate-400 text-sm`} />
                <span className="text-[11px] font-bold text-slate-600 uppercase tracking-tight">{typeInfo.label}</span>
            </div>
        );
    };

    const dateTemplate = (rowData) => {
        return (
            <div className="flex flex-col">
                <span className="font-black text-slate-700 text-sm">{new Date(rowData.movementDate).toLocaleDateString('tr-TR')}</span>
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{new Date(rowData.movementDate).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
            </div>
        );
    };

    const quantityTemplate = (rowData) => {
        const isIn = rowData.direction === 1;
        return (
            <div className="flex flex-col items-end">
                <span className={`font-black text-base tracking-tighter ${isIn ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {isIn ? '+' : '-'}{rowData.quantity.toLocaleString('tr-TR')}
                </span>
                <span className="text-[10px] text-slate-300 font-bold">Miktar</span>
            </div>
        );
    };

    const productTemplate = (rowData) => {
        return (
            <div className="flex flex-col">
                <span className="font-black text-slate-800 text-sm leading-tight">{rowData.productName}</span>
                <div className="flex items-center gap-1.5 ">
                    <i className="pi pi-home text-[10px] text-slate-700" />
                    <span className="text-[10px] text-slate-400 font-bold uppercase">{rowData.warehouseName}</span>
                </div>
            </div>
        );
    };

    // Calculate Today's Stats
    const today = new Date().toDateString();
    const todayIn = data.filter(d => d.direction === 1 && new Date(d.movementDate).toDateString() === today).reduce((acc, d) => acc + d.quantity, 0);
    const todayOut = data.filter(d => d.direction === 2 && new Date(d.movementDate).toDateString() === today).reduce((acc, d) => acc + d.quantity, 0);

    const header = (
        <div className="bg-slate-200 p-4 rounded-t-[2rem] border-b border-slate-100 space-y-4">
             <div className="flex items-center gap-6 pb-4">
                <label className="text-m font-bold text-slate-500 uppercase tracking-widest">Ürün Filtrele</label>
                    <AutoComplete
                        value={selectedProduct}
                        suggestions={filteredProducts}
                        completeMethod={searchProducts}
                        field="name"
                        placeholder="Ürün Adı (Min 3 Harf)"
                        className="w-full md:w-56"
                        inputClassName="w-full custom-input py-3"
                        onChange={(e) => setSelectedProduct(e.value)}
                        showClear
                    />
                </div>
            <div className="flex flex-col xl:flex-row justify-between gap-2">
               
                <div className="flex flex-wrap gap-6 flex-1">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <Calendar value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.value })} placeholder="Başlangıç" showIcon className="w-full md:w-40 custom-calendar" />
                    <Calendar value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.value })} placeholder="Bitiş" showIcon className="w-full md:w-40 custom-calendar" />
                   
                    <Dropdown value={filters.warehouseId} options={warehouses} onChange={(e) => setFilters({ ...filters, warehouseId: e.value })} placeholder="Depo" showClear className="w-full md:w-48 rounded-xl" />
                       <Button icon="pi pi-search" onClick={fetchData} className="!bg-indigo-600 border-none px-6 rounded-xl font-black text-xs" label="FİLTRELE" />
             
                    </div>
                 </div>
                <div className="flex items-center gap-2">
                    <span className="p-input-icon-left w-full md:w-64">
                        <i className="pi pi-search text-slate-400 pl-4" />
                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Hızlı ara..." className="w-full p-4 ml-3  !py-2 bg-white border-slate-200 rounded-xl font-bold text-sm" />
                    </span>
                    <Button icon="pi pi-refresh" onClick={() => { setFilters({ startDate: null, endDate: null, warehouseId: null }); setSelectedProduct(null); fetchData(); }} className="p-button-outlined ml-3 p-button-secondary rounded-xl h-[38px] w-[38px]" />
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-2 md:p-2 bg-white border border-slate-100 rounded-[3rem] animate-fade-in max-w-[1600px] mx-auto">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

            {/* Elite Page Header */}
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between mb-10 gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2.2rem] !bg-indigo-600 flex items-center justify-center shadow-2xl shadow-indigo-200 border-4 border-white">
                        <i className="pi pi-history text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-none uppercase tracking-tighter">Stok Hareketleri</h1>
                        <p className="text-slate-400 font-bold text-sm uppercase tracking-widest mt-2 italic flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            Global Envanter Denetim Günlüğü
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-3 w-full lg:w-auto">
                    <div className="flex-1 lg:flex-none bg-white p-4 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-4 px-6">
                        <div className="text-right">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest m-0 leading-tight">Bugünkü Giriş</p>
                            <h4 className="text-xl font-black text-emerald-600 m-0 leading-tight">+{todayIn.toLocaleString('tr-TR')}</h4>
                        </div>
                        <div className="w-px h-8 bg-slate-100" />
                        <div className="text-right">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest m-0 leading-tight">Bugünkü Çıkış</p>
                            <h4 className="text-xl font-black text-rose-600 m-0 leading-tight">-{todayOut.toLocaleString('tr-TR')}</h4>
                        </div>
                    </div>
                    <Button label="YENİ İŞLEM" icon="pi pi-plus" className="!bg-slate-900 border-none rounded-3xl px-6 h-[64px] font-black text-xs shadow-xl transition-all hover:scale-[1.02] hover:bg-indigo-600" onClick={() => navigate('/stock/adjustment')} />
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white shadow-2xl shadow-slate-400 border border-slate-100 rounded-[3rem] overflow-hidden">
                <DataTable
                    value={data}
                    loading={loading}
                    header={header}
                    globalFilter={globalFilter}
                    paginator
                    showGridlines
                    rows={15}
                    rowsPerPageOptions={[15, 25, 50, 100]}
                    className="p-datatable-sm elite-table"
                    emptyMessage="Herhangi bir stok hareketi kaydı bulunamadı."
                    rowHover
                    removableSort
                    responsiveLayout="stack"
                    breakpoint="960px"
                    dataKey="id"
                >
                    <Column field="movementDate" header="ZAMAN ÇİZELGESİ" body={dateTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 font-black text-[12px] uppercase p-2 tracking-widest" className="p-4" style={{ width: '150px' }} />
                    <Column field="direction" header="İŞLEM YÖNÜ" body={directionTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 font-black text-[12px] uppercase p-4 tracking-widest" className="p-2" style={{ width: '120px' }} />
                    <Column field="productName" header="ÜRÜN & DEPO" body={productTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 font-black text-[12px] uppercase p-4 tracking-widest" className="p-4" />
                    <Column field="movementType" header="HAREKET TÜRÜ" body={typeTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 font-black text-[12px] uppercase p-4 tracking-widest" className="p-4" />
                    <Column field="quantity" header="MİKTAR" body={quantityTemplate} sortable headerClassName="bg-transparent text-center text-slate-700 font-black text-[12px] uppercase p-4 tracking-widest  " className="p-4 text-right" style={{ width: '120px' }} />
                    <Column field="balanceAfterMovement" header="KALAN" body={(r) => (
                        <div className="flex flex-col items-end">
                            <span className="font-black text-slate-700 text-sm tracking-tighter">{r.balanceAfterMovement.toLocaleString('tr-TR')}</span>
                            <span className="text-[10px] text-slate-700 font-bold uppercase tracking-tighter">Bakiye</span>
                        </div>
                    )} sortable headerClassName="bg-transparent text-center text-slate-700 font-black text-[12px] uppercase p-4 tracking-widest" className="p-4" style={{ width: '120px' }} />
                    <Column field="description" header="SİSTEM NOTU" body={(r) => <span className="text-slate-700 font-medium text-[12px] italic">{r.description || '—'}</span>} headerClassName="bg-transparent text-center text-slate-700 font-black text-[12px] uppercase p-4 tracking-widest" className="p-2" />
                </DataTable>
            </div>

            <style>{`
                .elite-table .p-datatable-tbody > tr {
                    border-bottom: 1px solid #f1f5f9;
                    transition: all 0.2s;
                }
                .elite-table .p-datatable-tbody > tr:hover {
                    background: #f8fafc !important;
                    transform: translateX(4px);
                }
                .custom-calendar .p-inputtext, .custom-input {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 0.75rem;
                    font-weight: 700;
                    font-size: 0.875rem;
                    padding: 0.5rem 1rem;
                }
                .p-dropdown {
                    background: #f8fafc;
                    border-color: #e2e8f0;
                }
                .p-paginator {
                    background: transparent;
                    border: none;
                    padding: 2rem;
                }
            `}</style>
        </div>
    );
}
